import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { useFormik, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  IconButton,
  Box,
  Typography,
  Divider,
  Stack,
  CircularProgress,
  Alert,
  useMediaQuery,
  MenuItem,
  Autocomplete,
  FormControlLabel,
  Switch,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const validationSchema = Yup.object({
  field_label: Yup.string().required("Field label is required"),
  field_type: Yup.object().required("Field type is required"),
  location: Yup.string().required("Location is required"),
  parent_field_id: Yup.object()
    .when("field_type", {
      is: (field_type) => field_type?.value === "option",
      then: (schema) => schema.required("Parent field is required"),
      otherwise: (schema) => schema.notRequired(),
    })
    .nullable(),
  multiple: Yup.boolean().nullable(),
  mark_circle: Yup.boolean().nullable(),
});

const LOCATION_OPTIONS = [
  { value: "create", label: "Create" },
  { value: "sign", label: "Sign" },
];

export const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "selectGroup", label: "Select Group" },
  { value: "checkboxGroup", label: "Checkbox Group" },
  { value: "option", label: "Option" },
  { value: "designProAuto", label: "Design Pro Auto" },
  { value: "mobile", label: "Mobile" },
  { value: "email", label: "Email" },
  { value: "dollar", label: "Dollar" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "todayDate", label: "Today Date" },
  { value: "addressGroup", label: "Address Group" },
  { value: "mobileGroup", label: "Mobile Group" },
  { value: "multiline", label: "Multiline" },
  { value: "privateProvider", label: "Private Provider" },
  { value: "project_contact_details", label: "Project Contact Details" },
  { value: "designProfessional", label: "Design Professional" },
  { value: "feeSimple", label: "Fee Simple" },
  { value: "bonding", label: "Bonding" },
  { value: "mortgage", label: "Mortgage" },
  { value: "termite", label: "Termite" },
  { value: "full_title", label: "Full Title" },
  { value: "full_subTitle", label: "Full SubTitle" },
  { value: "total", label: "Total" },
  { value: "percentageCheckbox", label: "Percentage Checkbox" },
];

const NewFieldDialog = ({
  open,
  handleClose,
  getFields,
  selectedField,
  filter,
  searchField,
  setPage,
  getFormFieldsList,
}) => {
  const mobileSize = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const currentSession = useSelector((state) => state.sessions.currentSession);

  const [createFieldMessage, setCreateFieldMessage] = useState({
    type: "",
    message: "",
  });
  const [fields, setFields] = useState([]);

  const getFormFields = useCallback(async () => {
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/list`,
        { currentSession },
        { withCredentials: true },
      );

      if (res.data.success) {
        const fields = res.data.data?.fields;
        setFields(fields);
      }
    } catch (error) {
      console.error("An error occurred while fetching fields");
    }
  }, [currentSession]);

  useEffect(() => {
    getFormFields();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formik = useFormik({
    initialValues: {
      field_label: selectedField?.field_label ?? "",
      field_type:
        FIELD_TYPE_OPTIONS?.find(
          (opt) => opt.value === selectedField?.field_type,
        ) ?? null,
      location: selectedField?.location ?? "",
      parent_field_id: null,
      multiple: selectedField?.multiple ?? false,
      mark_circle: selectedField?.mark_circle ?? false,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      setCreateFieldMessage({ type: "", message: "" });
      let payload = {
        ...values,
        field_type: values?.field_type?.value || "",
        parent_field_id: values?.parent_field_id?.value || "",
        ...(selectedField ? { field_id: selectedField?.field_id } : {}),
      };

      if (values?.field_type?.value !== "option") {
        delete payload.parent_field_id;
      }
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BASE_URL}/nocApi/form-fields/upsert`,
          {
            currentSession: currentSession,
            field: payload,
          },
          {
            withCredentials: true,
          },
        );

        if (response?.data?.success) {
          setCreateFieldMessage({
            type: "success",
            message:
              response?.data?.message ||
              `Form field ${selectedField ? "updated" : "created"} successfully`,
          });
          resetForm();
          getFields(filter, 1, searchField);
          setPage(1);
          getFormFieldsList();
          handleClose();
        }
      } catch (error) {
        setCreateFieldMessage({
          type: "error",
          message:
            error?.response?.data?.message ||
            `An error occurred while ${selectedField ? "updating" : "creating"} field`,
        });
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    resetForm,
    setFieldValue,
  } = formik;

  const parentFields = useMemo(() => {
    const updatedfields = fields
      ?.filter(
        (field) =>
          field?.field_type === "selectGroup" ||
          field?.field_type === "checkboxGroup",
      )
      .map((field) => ({
        label: field?.field_label,
        value: field?.field_id,
      }));

    setFieldValue(
      "parent_field_id",
      updatedfields?.find((f) => f.value === selectedField?.parent_field_id) ||
        null,
    );

    return updatedfields;
  }, [fields, selectedField?.parent_field_id, setFieldValue]);

  const handleCloseModal = () => {
    resetForm();
    setCreateFieldMessage({ type: "", message: "" });
    handleClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleCloseModal}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { ...(mobileSize ? { margin: 0, width: "95%" } : {}) },
        },
      }}
    >
      <DialogTitle
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
            {selectedField ? "Update" : "Create"} Form field
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={handleCloseModal}
          disabled={isSubmitting}
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Divider />

      <FormikProvider value={formik}>
        <Box component="form" onSubmit={handleSubmit} noValidate>
          <DialogContent sx={{ px: 3, py: 2.5 }}>
            <Stack spacing={2.5}>
              <Stack direction="row" spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label="Field Label"
                  name="field_label"
                  value={values.field_label}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.field_label && Boolean(errors.field_label)}
                  helperText={touched.field_label && errors.field_label}
                />
              </Stack>

              <Stack direction="row" spacing={2}>
                <Autocomplete
                  value={values.field_type}
                  onChange={(event, newValue) => {
                    formik.setFieldValue("field_type", newValue);
                  }}
                  onBlur={handleBlur}
                  options={FIELD_TYPE_OPTIONS}
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      label="Select Field Type"
                      name="field_type"
                      error={touched.field_type && Boolean(errors.field_type)}
                      helperText={touched.field_type && errors.field_type}
                    />
                  )}
                />
                <TextField
                  select
                  fullWidth
                  size="small"
                  label="Select Location"
                  name="location"
                  value={values.location}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={touched.location && Boolean(errors.location)}
                  helperText={touched.location && errors.location}
                >
                  {LOCATION_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </TextField>
              </Stack>

              {values?.field_type?.value === "option" && (
                <Stack direction="row" spacing={2}>
                  <Autocomplete
                    value={values.parent_field_id}
                    onChange={(event, newValue) => {
                      formik.setFieldValue("parent_field_id", newValue);
                    }}
                    onBlur={handleBlur}
                    options={parentFields}
                    fullWidth
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        label="Select Parent Field"
                        name="parent_field_id"
                        error={
                          touched.parent_field_id &&
                          Boolean(errors.parent_field_id)
                        }
                        helperText={
                          touched.parent_field_id && errors.parent_field_id
                        }
                      />
                    )}
                  />
                </Stack>
              )}
              {values?.field_type?.value === "selectGroup" && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.multiple}
                      onChange={(e) =>
                        setFieldValue("multiple", e.target.checked)
                      }
                      disabled={isSubmitting}
                    />
                  }
                  label={"Allow Multiple"}
                />
              )}
              {(values?.field_type?.value === "selectGroup" ||
                values?.field_type?.value === "checkboxGroup") && (
                <FormControlLabel
                  control={
                    <Switch
                      checked={values.mark_circle}
                      onChange={(e) =>
                        setFieldValue("mark_circle", e.target.checked)
                      }
                      disabled={isSubmitting}
                    />
                  }
                  label={"Mark As Circle"}
                />
              )}

              {createFieldMessage?.message && (
                <Alert
                  severity={createFieldMessage?.type}
                  sx={{ borderRadius: 2 }}
                >
                  {createFieldMessage?.message}
                </Alert>
              )}
            </Stack>
          </DialogContent>

          <Divider />

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={handleCloseModal}
              variant="text"
              disabled={isSubmitting}
              sx={{ textTransform: "none", color: "text.secondary" }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              startIcon={
                isSubmitting ? (
                  <CircularProgress size={14} color="inherit" />
                ) : null
              }
              sx={{ textTransform: "none", borderRadius: 2, px: 3 }}
            >
              {isSubmitting ? "Saving…" : "Save Field"}
            </Button>
          </DialogActions>
        </Box>
      </FormikProvider>
    </Dialog>
  );
};

export default NewFieldDialog;
